import * as React from 'react';

import Popover from '@mui/material/Popover';
import { alpha, styled, useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

import Checkbox from '@mui/material/Checkbox';
import FilterListIcon from '@mui/icons-material/FilterList';
import { ReactComponent as FilterListIconActive } from './icons/filter_list_icon_active.svg';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import { Chip, IconButton, Snackbar, Tooltip } from "@mui/material";
import MenuItem from '@mui/material/MenuItem';
import Switch from "@mui/material/Switch";
import RemoteAutocomplete from '../components/RemoteAutocomplete';
import Box from '@mui/material/Box';
import Select from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';
import useCloseOnPanelChange from './utils/useCloseOnPanelChange';
import { cv } from '../themeVars';

// Tour tags are displayed by their friendly name throughout chat (the chips in
// the conversation list render `data.friendly_name || name`), so the filter
// pickers must label and match options the same way — e.g. the product_info tag
// named "17212/28" shows as "ONLY PARK". Some friendly names are whitespace-only
// placeholders, hence the trim-then-fallback.
const tourTagDisplayName = (tag) => {
    const friendly = typeof tag?.data?.friendly_name === 'string' ? tag.data.friendly_name.trim() : '';
    return friendly || tag?.name || '';
};

// CP-8239: participant tags span many categories, and bare friendly names are
// chaos in a mixed list — label them "Category: Name" (e.g. "Product Info:
// ONLY PARK"). The server's text query matches this same combined string, so
// the category is a valid search term too. (Internal Tags stay unprefixed —
// they're all one category.)
const tourTagCategoryDisplayName = (tag) => {
    const name = tourTagDisplayName(tag);
    const category = typeof tag?.category?.name === 'string' ? tag.category.name.trim() : '';
    return category ? `${category}: ${name}` : name;
};

// Popover (not Menu): there is no MenuList, so single-char keydowns can't
// trigger its typeahead focus-steal on the tag autocompletes (the CP-7979
// bug class) and the menu can lay its sections out in a multi-column grid.
const StyledPopover = styled((props) => (
    <Popover
        elevation={0}
        // CP-6445: MUI locks body scroll while open; that lock can outlive the popover
        // when a route change switches panels without a dismissing click.
        disableScrollLock
        anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
        }}
        transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
        }}
        {...props}
    />
))(({ theme }) => ({
    '& .MuiPaper-root': {
        borderRadius: 6,
        marginTop: theme.spacing(1),
        backgroundColor: cv('surface', "#FCFCFE"),
        color:
            theme.palette.mode === 'light' ? 'rgb(55, 65, 81)' : theme.palette.grey[300],
        boxShadow:
            'rgb(255, 255, 255) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 0px 0px 1px, rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px',
        '& .MuiMenuItem-root': {
            paddingTop: "0px",
            paddingBottom: "0px",
            // Beat MenuItem's mobile default (min-height: 48px below the sm
            // breakpoint) — min-height wins over the max-height cap below,
            // which blew each row up to 48px on phones.
            minHeight: "24px",
            maxHeight: "24px",
            '& .MuiSvgIcon-root': {
                fontSize: 18,
                //color: theme.palette.text.secondary,
                //marginRight: theme.spacing(1.5),
            },
            '&:active': {
                backgroundColor: alpha(
                    theme.palette.primary.main,
                    theme.palette.action.selectedOpacity,
                ),
            },
        },
        '& .MuiMenuItem-root.cp-message-activity-row': {
            minHeight: 0,
            maxHeight: 'none',
            alignItems: 'center',
            paddingTop: theme.spacing(0.5),
            paddingBottom: theme.spacing(0.5),
        },
        '& .MuiFormControlLabel-root': {
            width: "120%",
            marginRight: 0, 
            paddingRight: 4,
            '& .MuiTypography-root': {
                fontSize: ".9em",
                fontWeight: 500,
            },
            
        },
    },
}));

/** Tri-state message-activity filter: any (default), has messages, or does not have messages */
export const MSG_ACTIVITY_ANY = 'any';
export const MSG_ACTIVITY_HAS = 'has';
export const MSG_ACTIVITY_NONE = 'none';

// Slim track + 16px thumb that overflows (same idea as <Switch size="small">), widened for 3 stops.
const MESSAGE_ACTIVITY_THUMB_PX = 16;
// Even-valued height keeps flex centering inside the 24px hit area pixel-aligned (top = (24-10)/2 = 7).
// Using 9 here would put the track's top at y=7.5, which browsers round to 7 and visibly biases the
// thumb half a pixel low relative to the track midline.
const MESSAGE_ACTIVITY_TRACK_H = 9;
const MESSAGE_ACTIVITY_TRACK_W = 36;
const MESSAGE_ACTIVITY_HIT_H = 24;
/** Negative = move thumb up on the track (visual alignment vs flex-centered rail). */
const MESSAGE_ACTIVITY_THUMB_TOP_OFFSET_PX = -0.25;
/** CSS `left` on the thumb + slider overlay. `thumbOffsetX` subtracts this for ANY/HAS, so only the
 *  NONE ("No") stop moves when you change this; middle/right stays fixed. */
const MESSAGE_ACTIVITY_THUMB_ANCHOR_LEFT = -0.25;

const MESSAGE_ACTIVITY_MOTION = '280ms cubic-bezier(0.4, 0, 0.2, 1)';
const MESSAGE_ACTIVITY_CYCLE = [MSG_ACTIVITY_ANY, MSG_ACTIVITY_HAS, MSG_ACTIVITY_NONE];

function thumbOffsetX(v) {
    if (v === MSG_ACTIVITY_NONE) return -1;
    if (v === MSG_ACTIVITY_HAS) {
        return MESSAGE_ACTIVITY_TRACK_W - MESSAGE_ACTIVITY_THUMB_PX + 2 - MESSAGE_ACTIVITY_THUMB_ANCHOR_LEFT;
    }
    // ANY (center): translateX chosen so left + translateX = (TRACK_W - THUMB_PX) / 2 (independent of ANCHOR).
    return (MESSAGE_ACTIVITY_TRACK_W - MESSAGE_ACTIVITY_THUMB_PX) / 2 - MESSAGE_ACTIVITY_THUMB_ANCHOR_LEFT;
}

/** Tri-state switch: thumb left = No (red), center = Any (gray, default), right = Has (primary blue). */
function MessageActivityTriToggle({ label, value, onCommit }) {
    const theme = useTheme();
    const trackRef = React.useRef(null);

    const pickFromEvent = (clientX) => {
        const el = trackRef.current;
        if (!el) return;
        const rect = el.getBoundingClientRect();
        const t = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
        let next;
        if (t < 1 / 3) next = MSG_ACTIVITY_NONE;
        else if (t < 2 / 3) next = MSG_ACTIVITY_ANY;
        else next = MSG_ACTIVITY_HAS;
        if (next !== value) onCommit(next);
    };

    const cycleFromRow = () => {
        const i = MESSAGE_ACTIVITY_CYCLE.indexOf(value);
        const next = MESSAGE_ACTIVITY_CYCLE[(i + 1) % MESSAGE_ACTIVITY_CYCLE.length];
        onCommit(next);
    };

    /** Thumb is always under the same horizontal third as the current value, so zone-pick would no-op; cycle instead. */
    const handleMenuItemClick = (e) => {
        const trackEl = trackRef.current;
        if (!trackEl || !(e.target instanceof Node) || !trackEl.contains(e.target)) {
            cycleFromRow();
            return;
        }
        const rect = trackEl.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        const tx = thumbOffsetX(value);
        const thumbTop =
            (MESSAGE_ACTIVITY_HIT_H - MESSAGE_ACTIVITY_THUMB_PX) / 2 + MESSAGE_ACTIVITY_THUMB_TOP_OFFSET_PX;
        const thumbLeft = MESSAGE_ACTIVITY_THUMB_ANCHOR_LEFT + tx;
        const thumbPad = 4;
        const onThumb =
            x >= thumbLeft - thumbPad &&
            x <= thumbLeft + MESSAGE_ACTIVITY_THUMB_PX + thumbPad &&
            y >= thumbTop - thumbPad &&
            y <= thumbTop + MESSAGE_ACTIVITY_THUMB_PX + thumbPad;
        if (onThumb) {
            cycleFromRow();
            return;
        }
        pickFromEvent(e.clientX);
    };

    const isHas = value === MSG_ACTIVITY_HAS;
    const isNone = value === MSG_ACTIVITY_NONE;

    // Track stays a neutral thin rail when Any (matches MUI Switch unchecked);
    // tints to primary blue / red when active (matches MUI Switch checked-track tint).
    const trackColor = isHas
        ? alpha(theme.palette.primary.main, 0.5)
        : isNone
          ? alpha(theme.palette.error.main, 0.45)
          : cv('text-disabled', 'rgba(0, 0, 0, 0.38)');

    const thumbColor = isHas
        ? theme.palette.primary.main
        : isNone
          ? theme.palette.error.main
          : '#fafafa';

    const thumbTranslateX = thumbOffsetX(value);

    const ariaText = isNone ? 'No' : isHas ? 'Has' : 'Any';
    const ariaNow = isNone ? 0 : isHas ? 2 : 1;

    return (
        <MenuItem
            disableRipple
            className="cp-message-activity-row"
            sx={{ paddingRight: '4px' }}
            onClick={handleMenuItemClick}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    width: 'calc(100% + 32px)',
                    marginLeft: 0,
                }}
            >
                <Box component="span" sx={{ fontSize: '.9em', fontWeight: 500, flex: 1, minWidth: 0 }}>
                    {label}
                </Box>
                <Box
                    ref={trackRef}
                    data-tri-track
                    role="presentation"
                    sx={{
                        position: 'relative',
                        width: MESSAGE_ACTIVITY_TRACK_W,
                        height: MESSAGE_ACTIVITY_HIT_H,
                        marginLeft: '16px',
                        flexShrink: 0,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        marginRight: '4px',
                    }}
                >
                    <Box
                        sx={{
                            width: '100%',
                            height: MESSAGE_ACTIVITY_TRACK_H,
                            borderRadius: MESSAGE_ACTIVITY_TRACK_H / 2,
                            bgcolor: trackColor,
                            transition: `background-color ${MESSAGE_ACTIVITY_MOTION}`,
                            position: 'relative',
                            pointerEvents: 'none',
                        }}
                    >
                        {/* center detent */}
                        <Box
                            sx={{
                                position: 'absolute',
                                inset: 0,
                                margin: 'auto',
                                width: 2,
                                height: 2,
                                borderRadius: '50%',
                                bgcolor: 'rgba(255,255,255,0.7)',
                                opacity: value === MSG_ACTIVITY_ANY ? 0 : 0.9,
                                transition: `opacity ${MESSAGE_ACTIVITY_MOTION}`,
                                pointerEvents: 'none',
                            }}
                        />
                    </Box>
                    <Box
                        aria-hidden
                        sx={{
                            position: 'absolute',
                            top:
                                (MESSAGE_ACTIVITY_HIT_H - MESSAGE_ACTIVITY_THUMB_PX) / 2 +
                                MESSAGE_ACTIVITY_THUMB_TOP_OFFSET_PX,
                            left: MESSAGE_ACTIVITY_THUMB_ANCHOR_LEFT,
                            width: MESSAGE_ACTIVITY_THUMB_PX,
                            height: MESSAGE_ACTIVITY_THUMB_PX,
                            borderRadius: '50%',
                            bgcolor: thumbColor,
                            boxShadow:
                                '0px 1px 1px 0px rgba(0,0,0,0.2), 0px 2px 1px -1px rgba(0,0,0,0.14), 0px 1px 3px 0px rgba(0,0,0,0.12)',
                            transform: `translateX(${thumbTranslateX}px)`,
                            transition: `transform ${MESSAGE_ACTIVITY_MOTION}, background-color ${MESSAGE_ACTIVITY_MOTION}`,
                            pointerEvents: 'none',
                        }}
                    />
                    {/* Captures clicks over rail + thumb (thumb is non-interactive for hit-testing). */}
                    <Box
                        role="slider"
                        aria-valuenow={ariaNow}
                        aria-valuemin={0}
                        aria-valuemax={2}
                        aria-valuetext={ariaText}
                        aria-label={`${label}: ${ariaText}`}
                        sx={{
                            position: 'absolute',
                            top: 0,
                            left: MESSAGE_ACTIVITY_THUMB_ANCHOR_LEFT,
                            width: MESSAGE_ACTIVITY_TRACK_W - MESSAGE_ACTIVITY_THUMB_ANCHOR_LEFT + 2,
                            height: MESSAGE_ACTIVITY_HIT_H,
                            zIndex: 2,
                            cursor: 'pointer',
                            borderRadius: 1,
                        }}
                    />
                </Box>
            </Box>
        </MenuItem>
    );
}

export default function ChatListMenu(props) {
    const [anchorEl, setAnchorEl] = React.useState(null);
    // Horizontal space between the layout's <main> left edge and the filter
    // button, measured when the menu opens. The popover is right-aligned to
    // the button and must not extend past <main> — the fixed sidebar drawer
    // renders above it and would cover the leftmost column.
    const [availableWidth, setAvailableWidth] = React.useState(null);
    const open = Boolean(anchorEl);

    const handleClick = (event) => {
        const anchor = event.currentTarget;
        const mainEl = document.querySelector('main');
        const leftBound = mainEl ? mainEl.getBoundingClientRect().left : 0;
        setAvailableWidth(Math.max(0, anchor.getBoundingClientRect().right - leftBound - 12));
        setAnchorEl(anchor);
    };
    const handleClose = () => {
        setAnchorEl(null);
    };

    useCloseOnPanelChange(props.selectedScreen, handleClose);

    const handleOpenClosedFilterCheckboxClick = (event, boxLabel) => {
        if (event.target.checked !== undefined) {
            if (boxLabel === "closed") {
                props.resetChatList()
                props.setClosedStatusFilter(event.target.checked)
            }
            else if (boxLabel === "open") {
                props.resetChatList()
                props.setOpenStatusFilter(event.target.checked)
            }
            else if (boxLabel === "auto_closed") {
                props.resetChatList()
                props.setAutoClosedStatusFilter(event.target.checked)
            }   
        }
    }


    const handleNotificationsPausedFilterCheckboxClick = (event, boxLabel) => {
        if (event.target.checked !== undefined) {
            if (boxLabel === "paused") {
                props.resetChatList()
                props.setPausedNotificationsStatusFilter(event.target.checked)
            }
            else if (boxLabel === "not_paused") {
                props.resetChatList()
                props.setNotPausedNotificationsStatusFilter(event.target.checked)
            }
        }
    }

    /*
    const handleAdminFilterCheckboxClick = (event, obj) => {
        if (event.target.checked !== undefined) {
            props.resetChatList()
            if(!event.target.checked){
                props.setFilterByAssignedAdmin({
                    ...props.filterByAssignedAdmin,
                    excluded_assigned_to_admins: props.excludedAdmins.concat(obj)
                })
            }
            else{
                props.setFilterByAssignedAdmin({
                    ...props.filterByAssignedAdmin,
                    excluded_assigned_to_admins: props.excludedAdmins.filter(admin => admin.id != obj.id)
                })
            }
        }
    }
    */
    const handleActiveToursCheckboxClick = (event) => {
        if (event.target.checked !== undefined){
            props.resetChatList()
            props.setExcludedDateCategories({...props.excludedDateCategories, excludeActiveTours: !props.excludedDateCategories.excludeActiveTours})
        }
    }

    const handleUpcomingToursCheckboxClick = (event) => {
        if (event.target.checked !== undefined){
            props.resetChatList()
            props.setExcludedDateCategories({...props.excludedDateCategories, excludeUpcomingTours: !props.excludedDateCategories.excludeUpcomingTours})
        }
    }

    const handlePastToursCheckboxClick = (event) => {
        if (event.target.checked !== undefined){
            props.resetChatList()
            props.setExcludedDateCategories({...props.excludedDateCategories, excludePastTours: !props.excludedDateCategories.excludePastTours})
        }
    }

    // CP-8136: staff/guide-group channels are hidden server-side by default;
    // this switch opts back in. Only rendered when the operator actually has
    // staff chat groups (props.hasStaffChatGroups, from the list response).
    const handleStaffGroupsCheckboxClick = (event) => {
        if (event.target.checked !== undefined){
            props.resetChatList()
            props.setShowStaffGroups(!props.showStaffGroups)
        }
    }

    const onInternalTagsChange = async (ev, tags, reason) => {
        props.resetChatList()
        props.setFilteredInternalTags(tags)
    };
    
    const excludedAccessTagIds = React.useMemo(
        () => new Set((props.excludedAccessTags || []).map(t => t.id)),
        [props.excludedAccessTags]
    );

    const combinedAccessTagValue = React.useMemo(
        () => [...(props.filteredAccessTags || []), ...(props.excludedAccessTags || [])],
        [props.filteredAccessTags, props.excludedAccessTags]
    );

    const onAccessTagsChange = async (ev, tags, reason) => {
        props.resetChatList();
        if (reason === 'clear') {
            props.setFilteredAccessTags([]);
            props.setExcludedAccessTags([]);
            return;
        }
        const included = [];
        const excluded = [];
        for (const tag of tags) {
            if (excludedAccessTagIds.has(tag.id)) {
                excluded.push(tag);
            } else {
                included.push(tag);
            }
        }
        props.setFilteredAccessTags(included);
        props.setExcludedAccessTags(excluded);
    };

    const onAccessTagChipClick = (tag) => {
        props.resetChatList();
        if (excludedAccessTagIds.has(tag.id)) {
            props.setExcludedAccessTags((props.excludedAccessTags || []).filter(t => t.id !== tag.id));
            props.setFilteredAccessTags([...(props.filteredAccessTags || []), tag]);
        } else {
            props.setFilteredAccessTags((props.filteredAccessTags || []).filter(t => t.id !== tag.id));
            props.setExcludedAccessTags([...(props.excludedAccessTags || []), tag]);
        }
    };

    const accessTagRenderTags = React.useCallback((value, getTagProps) => {
        return value.map((tag, index) => {
            const { key, ...tagProps } = getTagProps({ index });
            const isExcluded = excludedAccessTagIds.has(tag.id);
            return (
                <Tooltip key={key} title="Click to toggle include / exclude" arrow enterDelay={600}>
                    <Chip
                        {...tagProps}
                        label={isExcluded ? `NOT ${tag.name}` : tag.name}
                        size="small"
                        onClick={(e) => { e.stopPropagation(); onAccessTagChipClick(tag); }}
                        sx={{
                            textDecoration: isExcluded ? 'line-through' : 'none',
                            backgroundColor: isExcluded ? cv('danger-tint', '#FDECEC') : undefined,
                            color: isExcluded ? cv('danger-text', '#C62828') : undefined,
                            borderColor: isExcluded ? cv('danger-border', '#E57373') : undefined,
                            border: isExcluded ? '1px solid' : undefined,
                            '&:hover': {
                                backgroundColor: isExcluded ? cv('danger-tint-strong', '#FBD9D9') : undefined,
                            },
                        }}
                    />
                </Tooltip>
            );
        });
    }, [excludedAccessTagIds, props.filteredAccessTags, props.excludedAccessTags]);

    const onParticipantTagsChange = async (ev, tags, reason) => {
        props.resetChatList()
        props.setFilteredParticipantTags(tags)
    };

    const handleOrderChannelsBySelectionChange = (event) => {
        if (event.target.value !== undefined){
            props.resetChatList()
            props.setOrderChannelsBy(event.target.value)
        }
    }

    const [copySnackbarOpen, setCopySnackbarOpen] = React.useState(false);

    const handleCopyFilterLink = () => {
        if (props.getFilterShareURL) {
            const url = props.getFilterShareURL();
            navigator.clipboard.writeText(url).then(() => {
                setCopySnackbarOpen(true);
            }).catch(() => {
                window.prompt('Copy this link:', url);
            });
        }
    };

    const clearAll = () => {
        props.clearFilters()
    }

    const filtersHaveChanged = () => {
        return props.filtersHaveChanged();
    }

    // Two side-by-side columns fit the whole menu on a 768px-tall viewport;
    // when the viewport (or the content area right of the sidebar) is too
    // narrow for two readable columns, fall back to the legacy single
    // scrolling column. Between 560px and the full 600px the grid just
    // renders slightly narrower columns.
    const canFitColumns = availableWidth === null || availableWidth >= 560;
    const isWide = useMediaQuery('(min-width:780px)') && canFitColumns;
    const menuWidth = isWide ? Math.min(600, availableWidth ?? 600) : 300;

    return (
        <div>
            <Tooltip title={'Filter/Hide'} arrow placement="top"><IconButton id="demo-customized-button"
                aria-controls={open ? 'demo-customized-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={open ? 'true' : undefined}
                onClick={handleClick}
                variant="contained"
                style={{ zIndex: 10, width: "2.5rem", height: "2.5rem", position: 'absolute', top: '65px', right: '0.5rem' }}
            >
                {filtersHaveChanged() ? <FilterListIconActive style={{ color: cv('accent', '#007AFF'), fontSize: "1em" }} /> : <FilterListIcon style={{ color: cv('accent', '#007AFF'), fontSize: "1em" }} />}
            </IconButton></Tooltip>

            <StyledPopover
                id="demo-customized-menu"
                aria-labelledby="demo-customized-button"
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
            >
                <Box sx={{ width: menuWidth, maxHeight: isWide ? 'none' : '80vh', overflowY: isWide ? 'visible' : 'auto' }}>
                <Box sx={{ display: 'grid', gridTemplateColumns: isWide ? 'repeat(2, 1fr)' : '1fr' }}>
                {/* Column 1 — how the list is shaped: tour timing, status, notifications, ordering */}
                <Box sx={{ minWidth: 0, paddingBottom: '8px' }}>
                <div style={{ textAlign: "left", fontWeight: 600, marginLeft: "16px", marginBottom: "1px", marginTop: "8px" }}>Show / Hide</div>
                <FormGroup style={{}}>
                    <MenuItem style={{ paddingRight: '4px' }}>
                        <FormControlLabel
                            sx={{
                                justifyContent: 'space-between',
                                display: 'flex',
                                marginLeft: 0,
                                width: 'calc(100% + 32px)',
                                paddingRight: '0px',
                                '& .MuiFormControlLabel-label': {
                                    marginRight: 'auto'
                                },
                                '& .MuiSwitch-root': {
                                    marginLeft: '16px',
                                    flexShrink: 0
                                }
                            }}
                            control={<Switch checked={(props.excludedDateCategories.excludeActiveTours === false)} size="small" />}
                            label="Active tours"
                            labelPlacement="start"
                            onClick={handleActiveToursCheckboxClick}
                        />
                    </MenuItem>
                    <MenuItem style={{ paddingRight: '4px' }}>
                        <FormControlLabel
                            sx={{
                                justifyContent: 'space-between',
                                display: 'flex',
                                marginLeft: 0,
                                width: 'calc(100% + 32px)',
                                paddingRight: '0px',
                                '& .MuiFormControlLabel-label': {
                                    marginRight: 'auto'
                                },
                                '& .MuiSwitch-root': {
                                    marginLeft: '16px',
                                    flexShrink: 0
                                }
                            }}
                            control={<Switch checked={(props.excludedDateCategories.excludeUpcomingTours === false)} size="small" />}
                            label="Upcoming tours"
                            labelPlacement="start"
                            onClick={handleUpcomingToursCheckboxClick}
                        />
                    </MenuItem>
                    <MenuItem style={{ paddingRight: '4px' }}>
                        <FormControlLabel
                            sx={{
                                justifyContent: 'space-between',
                                display: 'flex',
                                marginLeft: 0,
                                width: 'calc(100% + 32px)',
                                paddingRight: '0px',
                                '& .MuiFormControlLabel-label': {
                                    marginRight: 'auto'
                                },
                                '& .MuiSwitch-root': {
                                    marginLeft: '16px',
                                    flexShrink: 0
                                }
                            }}
                            control={<Switch checked={(props.excludedDateCategories.excludePastTours === false)} size="small" />}
                            label="Past tours"
                            labelPlacement="start"
                            onClick={handlePastToursCheckboxClick}
                        />
                    </MenuItem>
                    {props.hasStaffChatGroups ?
                    <MenuItem style={{ paddingRight: '4px' }}>
                        <FormControlLabel
                            sx={{
                                justifyContent: 'space-between',
                                display: 'flex',
                                marginLeft: 0,
                                width: 'calc(100% + 32px)',
                                paddingRight: '0px',
                                '& .MuiFormControlLabel-label': {
                                    marginRight: 'auto'
                                },
                                '& .MuiSwitch-root': {
                                    marginLeft: '16px',
                                    flexShrink: 0
                                }
                            }}
                            control={<Switch checked={(props.showStaffGroups === true)} size="small" />}
                            label="Guide groups"
                            labelPlacement="start"
                            onClick={handleStaffGroupsCheckboxClick}
                        />
                    </MenuItem>
                    : null}
                </FormGroup>
                <div style={{ textAlign: "left", fontWeight: 600, marginLeft: "16px", marginBottom: "1px", marginTop: "8px" }}>Status</div>
                <FormGroup>
                    <MenuItem style={{ paddingRight: 0 }}>
                        <FormControlLabel control={<Checkbox checked={props.openStatusFilter} />}
                            label={"Open"}
                            onClick={(ev) => handleOpenClosedFilterCheckboxClick(ev, "open")}
                        />
                    </MenuItem>
                    <MenuItem style={{ paddingRight: 0 }}>
                        <FormControlLabel control={<Checkbox checked={props.closedStatusFilter} />}
                            label={"Closed"}
                            onClick={(ev) => handleOpenClosedFilterCheckboxClick(ev, "closed")}
                        />
                    </MenuItem>
                    <MenuItem style={{ paddingRight: 0 }}>
                        <FormControlLabel control={<Checkbox checked={props.autoClosedStatusFilter} />}
                            label={"Auto-closed"}
                            onClick={(ev) => handleOpenClosedFilterCheckboxClick(ev, "auto_closed")}
                        />
                    </MenuItem>
                </FormGroup>
                <div style={{ textAlign: "left", fontWeight: 600, marginLeft: "16px", marginBottom: "1px", marginTop: "8px" }}>Notifications Status</div>
                <FormGroup>
                    <MenuItem style={{ paddingRight: 0 }}>
                        <FormControlLabel control={<Checkbox checked={props.pausedNotificationsStatusFilter} />}
                            label={"Paused"}
                            onClick={(ev) => handleNotificationsPausedFilterCheckboxClick(ev, "paused")}
                        />
                    </MenuItem>
                    <MenuItem style={{ paddingRight: 0 }}>
                        <FormControlLabel control={<Checkbox checked={props.notPausedNotificationsStatusFilter} />}
                            label={"Not Paused"}
                            onClick={(ev) => handleNotificationsPausedFilterCheckboxClick(ev, "not_paused")}
                        />
                    </MenuItem>
                </FormGroup>
                <Tooltip title="default ordering is tour departure" arrow placement="top"><div style={{ textAlign: "left", fontWeight: 600, marginLeft: "16px", marginTop: "8px", display: "inline-block" }}>Order Channels By</div></Tooltip>
                <FormGroup style={{paddingBottom: "8px"}}>
                    <FormControl size="small" style={{ textAlign: "left", fontWeight: 600, marginLeft: "16px", marginRight: "16px", marginTop: "4px"}}>
                        <Select
                            labelId="demo-simple-select-label"
                            id="demo-simple-select"
                            value={props.orderChannelsBy}
                            //label="Order By"
                            onChange={handleOrderChannelsBySelectionChange}
                        >
                            {!props.isRecentActivityView && <MenuItem value={"departure"}>Departure</MenuItem>}
                            <MenuItem value={"newest_msgs"}>Newest Messages</MenuItem>
                            <MenuItem value={"oldest_msgs"}>Oldest Messages</MenuItem>
                        </Select>
                    </FormControl>
                </FormGroup>
                </Box>
                {/* Column 2 — narrowing by content: the tri-state (Any/Has/No) controls, then tags */}
                <Box sx={{ minWidth: 0, paddingBottom: '8px', borderLeft: isWide ? `1px solid ${cv('border', '#ECEDF1')}` : 'none' }}>
                <div style={{ textAlign: "left", fontWeight: 600, marginLeft: "16px", marginBottom: "1px", marginTop: "8px" }}>Show Only</div>
                <MessageActivityTriToggle
                    label="Unread messages"
                    value={props.unreadFilter}
                    onCommit={(v) => {
                        props.resetChatList();
                        props.setUnreadFilter(v);
                    }}
                />
                <MessageActivityTriToggle
                    label="Users with missing data"
                    value={props.missingInfoFilter}
                    onCommit={(v) => {
                        props.resetChatList();
                        props.setMissingInfoFilter(v);
                    }}
                />
                {props.hasWebWidget ? (
                    // The chatbot handed the conversation off (holding message / the visitor
                    // asked for a person) and nobody has replied yet: right = only those (the
                    // queue an agent works), left = everything except those. Handoffs only
                    // exist in web-widget conversations, so operators without a widget never
                    // see this row (hasWebWidget, from ChatList's bootstrap read).
                    <MessageActivityTriToggle
                        label="Web chat needs a person"
                        value={props.needsPersonFilter}
                        onCommit={(v) => {
                            props.resetChatList();
                            props.setNeedsPersonFilter(v);
                        }}
                    />
                ) : null}
                <div style={{ textAlign: "left", fontWeight: 600, marginLeft: "16px", marginTop: "8px" }}>Message Activity</div>
                <MessageActivityTriToggle
                    label="Guest messages"
                    value={props.hasGuestMessagesFilter}
                    onCommit={(v) => {
                        props.resetChatList();
                        props.setHasGuestMessagesFilter(v);
                    }}
                />
                <MessageActivityTriToggle
                    label="Guide messages"
                    value={props.hasGuideMessagesFilter}
                    onCommit={(v) => {
                        props.resetChatList();
                        props.setHasGuideMessagesFilter(v);
                    }}
                />
                <MessageActivityTriToggle
                    label="Staff messages"
                    value={props.hasStaffMessagesFilter}
                    onCommit={(v) => {
                        props.resetChatList();
                        props.setHasStaffMessagesFilter(v);
                    }}
                />
                <MessageActivityTriToggle
                    label="AI auto-replies"
                    value={props.hasAIAutoRepliesFilter}
                    onCommit={(v) => {
                        props.resetChatList();
                        props.setHasAIAutoRepliesFilter(v);
                    }}
                />
                <MessageActivityTriToggle
                    label="Automated notifications"
                    value={props.hasAutomatedNotificationsFilter}
                    onCommit={(v) => {
                        props.resetChatList();
                        props.setHasAutomatedNotificationsFilter(v);
                    }}
                />
                <div style={{ textAlign: "left", fontWeight: 600, marginLeft: "16px", marginTop: "8px" }}>Internal Tags</div>
                <FormGroup style={{paddingLeft: "16px", paddingRight: "16px", paddingBottom: "8px", marginTop: "-6px"}}>
                    <RemoteAutocomplete
                        disabled={props.disabled}
                        multiple
                        resource={"tour_tag"}
                        apiFilters={[{ field: 'category_code', op: 'equals', value: "chat_internal" }]}
                        urlSuffix={null}
                        label={"Internal Tags"}
                        variant={props.variant || 'outlined'} size={props.size || 'small'}
                        fullWidth
                        filterSelected
                        value={props.filteredInternalTags || []}
                        defaultValue={props.filteredInternalTags || []}
                        onChange={onInternalTagsChange}
                        helperText={"Internal Tags"}
                        style={{ marginTop: "12px" }}
                        //onQueryChange={setTagSearchValue}
                        clearOnBlur={false}
                        //refreshKey={refreshKey}
                        // Same as the Participant Tags picker below: the server matches
                        // on friendly name too, so don't re-filter client-side.
                        filterOptions={(options) => options}
                        getOptionLabel={tourTagDisplayName}
                        renderOption={React.useCallback((props, option, { inputValue }) => {
                            function boldString(str, substr) {
                                // Escape regex specials — typed "(" etc. must not crash
                                // the RegExp constructor.
                                const strRegExp = new RegExp(substr.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'ig')
                                return str.replace(strRegExp, '<b style=color:black !important;>$&</b>')
                            }
                            return (
                                <Box component='li' {...props}>
                                    <div style={{}} dangerouslySetInnerHTML={{ __html: boldString(tourTagDisplayName(option), inputValue) }}/>
                                </Box>
                            )
                        })}
                    />
                </FormGroup>
                <div style={{ textAlign: "left", fontWeight: 600, marginLeft: "16px", marginTop: "8px" }}>Access Tags</div>
                <FormGroup style={{paddingLeft: "16px", paddingRight: "16px", paddingBottom: "8px", marginTop: "-6px"}}>
                    <RemoteAutocomplete
                        disabled={props.disabled}
                        multiple
                        resource={"access_tag"}
                        params={{limit_to_req_user_chat_atags: true}}
                        //apiFilters={[{ field: 'category_code', op: 'equals', value: "chat_internal" }]}
                        urlSuffix={null}
                        label={"Access Tags"}
                        variant={props.variant || 'outlined'} size={props.size || 'small'}
                        fullWidth
                        filterSelected
                        value={combinedAccessTagValue}
                        defaultValue={combinedAccessTagValue}
                        onChange={onAccessTagsChange}
                        renderTags={accessTagRenderTags}
                        helperText={"Access Tags"}
                        style={{ marginTop: "12px" }}
                        //onQueryChange={setTagSearchValue}
                        clearOnBlur={false}
                        //refreshKey={refreshKey}
                        renderOption={React.useCallback((props, option, { inputValue }) => {
                            function boldString(str, substr) {
                                // Escape regex specials — typed "(" etc. must not crash
                                // the RegExp constructor.
                                const strRegExp = new RegExp(substr.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'ig')
                                return str.replace(strRegExp, '<b style=color:black !important;>$&</b>')
                            }
                            return (
                                <Box component='li' {...props}>
                                    <div style={{}} dangerouslySetInnerHTML={{ __html: boldString(option.name, inputValue) }}/>
                                </Box>
                            )
                        })}
                    />
                </FormGroup>
                <div style={{ textAlign: "left", fontWeight: 600, marginLeft: "16px", marginTop: "8px" }}>Participant Tags</div>
                <FormGroup style={{paddingLeft: "16px", paddingRight: "16px", paddingBottom: "8px", marginTop: "-6px"}}>
                    <RemoteAutocomplete
                        disabled={props.disabled}
                        multiple
                        resource={"tour_tag"}
                        // Participant tags are every tag category except the
                        // internal/conversation one (same rule as AI training scope).
                        apiFilters={[{ field: 'category_code', op: 'not', value: "chat_internal" }]}
                        urlSuffix={null}
                        label={"Participant Tags"}
                        variant={props.variant || 'outlined'} size={props.size || 'small'}
                        fullWidth
                        filterSelected
                        value={props.filteredParticipantTags || []}
                        defaultValue={props.filteredParticipantTags || []}
                        onChange={onParticipantTagsChange}
                        helperText={"Participant Tags"}
                        style={{ marginTop: "12px" }}
                        clearOnBlur={false}
                        // The backend text query matches data.friendly_name, name and
                        // the "Category: Name" label; MUI's default client-side filter
                        // only sees the label, so it would drop server matches whose
                        // typed text is in the friendly name but not the name (or vice
                        // versa). Trust the server's filtering instead.
                        filterOptions={(options) => options}
                        getOptionLabel={tourTagCategoryDisplayName}
                        renderOption={React.useCallback((props, option, { inputValue }) => {
                            function boldString(str, substr) {
                                // Escape regex specials — tag/category names with "(" etc.
                                // in the typed text must not crash the RegExp constructor.
                                const strRegExp = new RegExp(substr.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'ig')
                                return str.replace(strRegExp, '<b style=color:black !important;>$&</b>')
                            }
                            return (
                                <Box component='li' {...props}>
                                    <div style={{}} dangerouslySetInnerHTML={{ __html: boldString(tourTagCategoryDisplayName(option), inputValue) }}/>
                                </Box>
                            )
                        })}
                    />
                </FormGroup>
                </Box>
                </Box>
                {/* Footer spans all columns */}
                <div style={{ textAlign: "left", fontWeight: 400, borderTop: `1px solid ${cv('border', '#ECEDF1')}`, padding: "9px 16px", display: "flex", justifyContent: "space-between" }}>
                    <span style={{ cursor: "pointer" }} onClick={clearAll}><u>Clear all</u></span>
                    <span style={{ cursor: "pointer", display: "inline-flex", alignItems: "center", gap: "4px" }} onClick={handleCopyFilterLink}>
                        <u>Copy link</u>
                    </span>
                </div>
                </Box>

            </StyledPopover>
            <Snackbar
                open={copySnackbarOpen}
                autoHideDuration={2000}
                onClose={() => setCopySnackbarOpen(false)}
                message="Filter link copied to clipboard"
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            />
        </div>
    );
}
